import { useEffect } from 'react'
import {
  TemplateActionNames,
  DefaultModalContainerNames,
} from '../../consts/defaultConfigValues/defaultConstants'
import {
  IPlaceOrderFlow,
  LoadersType,
  PlaceOrderFlowEntry,
  UnresolvedRecord,
} from '../../interfaces'
import {
  clearPlaceOrderActions,
  nextPlaceOrderAction,
  setPlaceOrderFlow,
} from '../../redux/slices/cart'
import { addAlert } from '../../redux/slices/config'
import {
  AlertMessageType,
  getAlertErrorMessage,
} from '../../utils/getAlertErrorMessage'
import { toCinnamonAction } from '../../utils/processActions'
import { prepareBeforeOrderQueue } from '../../utils/workflowStepDuplicates'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import type {
  IWorkflowRunStepArgs,
  RunWorkflowStep,
  WorkflowStepResult,
} from '../workflow/useWorkflowStep'
import { useValidatePlaceOrder } from '../usePlaceOrder'
import { usePushView } from '../usePushView'
import { useFrequentlyBoughtTogether } from './useFrequentlyBoughtTogether'
import useLinkedProducts from '../linkedProducts/useLinkedProducts'

interface IExecuteCartActions
  extends Pick<IPlaceOrderFlow, 'actions' | 'pushAfter'> {}

export interface UsePlaceOrderFlowOptions {
  /**
   * `EventManager`'s `runStep`, which runs a queued
   * `templateActions.workflow.runStep` (COM-1296). Without it such an entry
   * fails closed, as a step that failed with no handler does.
   */
  runStep?: RunWorkflowStep
}

/**
 * Whether a failed before-step's run ran a handler for that failure. Without
 * one, the queue shows the generic `workflowStep.failed` alert.
 *
 * Read off the result (`handled`), never off this queue's own args: when the
 * queue joins a run already in flight (a button started the step), the
 * handlers that ran were that caller's, and the queue's args were ignored.
 * No result (no `runStep` to run it with) ran nothing.
 */
const isFailureHandled = (result: WorkflowStepResult | undefined): boolean => {
  const isFailure =
    result?.kind === 'timeout' ||
    result?.kind === 'error' ||
    result?.kind === 'unmapped'
  if (!isFailure) {
    return false
  }
  return result.handled
}

/**
 * Two checks keep the stepping effect from starting a queue head twice
 * (COM-1447). This is the one place the reasoning lives; the other comments
 * and `.claude/CLAUDE.md` point here.
 *
 * Every `EventManager` mounts its own `usePlaceOrderFlow` over the one shared
 * `cartSlice.placeOrderFlow`, and each instance's stepping effect runs
 * `actions[0]` when it mounts over a live queue. A head that parks the queue
 * (a step's `outcome` or `stopped`, the recommendations or linked-products
 * modal) is still the head when its handler pushes another screen, and a
 * screen whose container `type` differs mounts a new instance. That instance
 * ran the head again: a second `RunWorkflowStep` with no tap, which a
 * stateful step then answers wrongly on the shopper's real next tap.
 *
 * - **The queue must still be the store's.** The effect skips unless the
 *   `actions` this instance rendered is still the array in the store. React
 *   runs a deleted component's passive cleanups before the new components'
 *   mount effects in the same commit (`flushPassiveEffectsImpl` calls
 *   `commitPassiveUnmountEffects`, deletions included, before
 *   `commitPassiveMountEffects`; checked in the installed react-dom 18.3.1,
 *   the same path on both root types). So when the cart is replaced, its
 *   cleanup has cleared the queue by the time the new screen's effect runs,
 *   but that screen rendered the old array. This check is also the only one
 *   that covers a handler advancing and pushing in one render (a click on
 *   the app's legacy root): the new screen rendered the advanced queue,
 *   whose head nobody has started, and running it would call `placeOrder()`
 *   over a queue the cart's cleanup had emptied, `pushAfter` included, so
 *   the order would be created with nowhere to push (COM-858's shape). A
 *   skipped instance that stays mounted re-renders with the store's queue
 *   and runs that, so skipping a stale render loses nothing.
 * - **`startedQueues`: a head runs once per queue array.** While the store
 *   still holds the same array the first check passes: a second instance
 *   mounting while the first is still up, or the effect re-running over the
 *   same queue. This set records each array whose head has started. It is
 *   module-level because the instance that has to skip is the one that did
 *   not start the head, and a `WeakSet` so marking dispatches nothing and a
 *   dropped queue is collected. It relies on every write that leaves a head
 *   making a new array: `setPlaceOrderFlow` copies its payload, and Immer
 *   allocates on `nextPlaceOrderAction` and `clearPlaceOrderActions`. The
 *   copy is load-bearing: a CTA handing back the previous tap's array would
 *   otherwise find its head marked and do nothing. The one write that keeps
 *   identity, `nextPlaceOrderAction` on an empty queue, leaves no head.
 *   `redux/slices/cart/tests/placeOrderFlowArrays.test.ts` pins all of it.
 *
 * Consequences:
 * - An authored `templateActions.clearPlaceOrderFlow` in an outcome is no
 *   longer needed to stop the re-run, and stays harmless.
 * - A `placeOrderFlowNext` from a dialog tapped later, on the cart or on a
 *   screen of the cart's own `type`, advances once. After a type-changing
 *   navigation the queue is already cleared and nothing runs, as before.
 * - The set would also stop a `loading` edge from re-invoking a pending head
 *   if the effect's deps ever grew, so `tests/usePlaceOrderFlow.test.tsx`
 *   can no longer catch that. Keep the deps as they are.
 * - Under `<StrictMode>` on a concurrent root (the suite only; the app mounts
 *   through the legacy root, see the COM-974 note in `.claude/CLAUDE.md`), a
 *   head current at mount runs on the first invocation, the simulated
 *   unmount's cleanup then clears the queue, and the second invocation
 *   skips. So it runs once, and the queue does not continue past it.
 *
 * Tests: `hooks/cart/tests/usePlaceOrderFlowRunOnce.test.tsx`.
 */
const startedQueues = new WeakSet<PlaceOrderFlowEntry[]>()

export const usePlaceOrderFlow = ({
  runStep,
}: UsePlaceOrderFlowOptions = {}) => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const { actions, pushAfter, isDone } = useAppSelector(
    (state) => state.cartSlice.placeOrderFlow,
  )
  /*
   * Whether any loader is up that holds the push. `components/Splash.tsx`
   * renders from `defaultSlice.loading`, which is "any loader at all"; this is
   * that list with `WORKFLOW_STEP` left out **only while an after-order list
   * holds the push** (`ordersSlice.afterOrderRunning`). See the push effect
   * for what the gate buys (COM-1111) and why an after-step's `WORKFLOW_STEP`
   * must not hold it (COM-1296). A boolean, so the push effect's deps are
   * unchanged.
   *
   * `RELOAD_PAGE` never holds it (COM-1521). That splash is up while a
   * `templateActions.reloadPage` waits for cart writes, and the page then
   * reloads. A flow that finishes in that window pushes as it did before the
   * splash existed, and a reload with no `path` then loads the pushed URL:
   * after a created order that is the confirmation screen, not the cart.
   */
  const loading = useAppSelector((state) => {
    const { afterOrderRunning } = state.ordersSlice
    return state.defaultSlice.loaders.some((name) => {
      if (name === LoadersType.RELOAD_PAGE) {
        return false
      }
      return name !== LoadersType.WORKFLOW_STEP || !afterOrderRunning
    })
  })

  // hooks
  const pushView = usePushView()
  const getFrequentlyBoughtTogether = useFrequentlyBoughtTogether()
  const placeOrder = useValidatePlaceOrder()
  const { getLinkedProducts } = useLinkedProducts()

  // clear place order actions
  const placeOrderFlowClear = () => {
    dispatch(clearPlaceOrderActions())
  }

  // next place order flow
  const placeOrderFlowNext = () => {
    dispatch(nextPlaceOrderAction())
  }

  /**
   * Runs the step on `EventManager`'s instance. A run of the same step
   * already in flight there, from a button or an earlier queue, is joined
   * by `runStep` itself, so this queue gets that run's verdict (COM-1296).
   */
  const runQueuedStepRun = (
    args: IWorkflowRunStepArgs,
  ): Promise<WorkflowStepResult | undefined> => {
    if (!runStep) {
      return Promise.resolve(undefined)
    }
    return runStep(args)
  }

  /**
   * Runs a queued `templateActions.workflow.runStep`, a before-step, and
   * decides what the queue does once it has ended (COM-1296).
   *
   * - `sent` (no `wait`, the trigger was accepted): go on to the next entry.
   * - `outcome`: the step's `onOutcome` decided, and the queue does nothing
   *   more. An `onOutcome` that continues authors
   *   `templateActions.placeOrderFlowNext`, now or from a dialog it opens,
   *   exactly as the confirm dialog continues the queue after
   *   `CART_PLACE_ORDER`. One that does not leaves the queue parked on this
   *   entry. **Parked is not hung**: the step's loader came down before its
   *   handler ran, nothing else is raised, and the next tap of the CTA
   *   dispatches `setPlaceOrderFlow`, which starts the queue again from its
   *   first entry, this step included. No order is placed in between.
   *   A screen its handler pushes does not run it again (`startedQueues`).
   * - `stopped`: no handler ran. Either the cart unmounted, and its cleanup
   *   has cleared the queue already, or the run was superseded on this
   *   instance (see `runStep`); the queue parks as above.
   * - `timeout`, `error` or `unmapped`, or no `runStep` to run it with:
   *   **fail closed**. Stop the queue, so the order is never placed without
   *   its precondition answering, and leave the shopper on the cart. If the
   *   run ran a handler for that failure (`handled`), it has said what
   *   happened; otherwise show the generic `workflowStep.failed` alert. The
   *   run's own `handled`, not this entry's args: a joined run ran the
   *   original caller's handlers, and an empty list ran nothing.
   *
   * The verdict applies only while the queue is still the one this step
   * started on. A handler that advanced it, a new tap that replaced it, or
   * the unmount cleanup all change `actions`, and then the verdict is not
   * this step's to act on. A new tap whose queue reaches the same step while
   * the old run is in flight joins that run rather than starting one (in
   * `runStep`: a second run of a pending step returns the run in flight), so
   * the verdict is still acted on, once, by the queue that is current. A
   * button that started the step with `runStep` is joined the same way.
   */
  const runQueuedStep = async (rawArgs: UnresolvedRecord | undefined) => {
    const args = (rawArgs ?? {}) as unknown as IWorkflowRunStepArgs
    const actionsAtStart = store.getState().cartSlice.placeOrderFlow.actions
    const result = await runQueuedStepRun(args)

    const flow = store.getState().cartSlice.placeOrderFlow
    // `!flow.isDone` is defensive: today `isDone` turns true only in
    // `nextPlaceOrderAction`, which always writes a new `actions` array, so
    // the identity check already catches it. It keeps the guard right if a
    // reducer ever sets `isDone` without replacing the array.
    const isStillOnThisStep = flow.actions === actionsAtStart && !flow.isDone
    if (!isStillOnThisStep) {
      return
    }
    if (result?.kind === 'sent') {
      placeOrderFlowNext()
      return
    }
    if (result?.kind === 'outcome' || result?.kind === 'stopped') {
      return
    }

    placeOrderFlowClear()
    if (isFailureHandled(result)) {
      return
    }
    const message = getAlertErrorMessage(
      AlertMessageType.WorkflowStepFailed,
      store.getState().defaultSlice.config?.texts,
    )
    if (message) {
      dispatch(addAlert({ message }))
    }
  }

  // hooks map. Each hook is handed its entry's `args`; only the runStep one
  // reads them.
  const hooksMap: Record<string, (args?: UnresolvedRecord) => void> = {
    [TemplateActionNames.CART_GET_FREQUENTLY_BOUGHT_TOGETHER]: () => {
      getFrequentlyBoughtTogether({
        modalName: DefaultModalContainerNames.RECOMMENDED_PRODUCTS,
      })
    },
    [TemplateActionNames.CART_PLACE_ORDER]: () => {
      placeOrder()
    },
    [TemplateActionNames.LINKED_PRODUCTS_GET_ITEMS]: () => {
      getLinkedProducts()
    },
    [TemplateActionNames.WORKFLOW_RUN_STEP]: (args) => {
      runQueuedStep(args)
    },
  }

  // set place order flow. A `runStep` after `CART_PLACE_ORDER` or listed
  // twice is dropped here, once, before the queue starts (see
  // `prepareBeforeOrderQueue`), so the stepping effect never sees it and its
  // deps are untouched.
  const placeOrderFlow = (args: IExecuteCartActions) => {
    const { actions: newActions, pushAfter: newPushAfter } = args
    const queue = prepareBeforeOrderQueue(newActions)
    dispatch(setPlaceOrderFlow({ actions: queue, pushAfter: newPushAfter }))
  }

  /*
   * Step through the flow's actions.
   *
   * **Its deps must not grow.** This used to be one effect with the push
   * below, and merging them back is what the split exists to prevent: the push
   * is gated on `loading`, and a `loading` edge re-entering this branch
   * re-invokes a *pending* action — a second `placeOrder()`, which opens the
   * confirm dialog's validations again mid-flow. Apart from the one below and
   * `RELOAD_PAGE`, no loader moves during the stepping phase today: the other
   * `addLoader` sites raise `STORE_CONFIG`, `INITIAL_DATA` and
   * `CREATE_ORDER`, and no hook in `hooksMap` other than the runStep one
   * reaches any loader at all. A step's handler can emit a
   * `templateActions.reloadPage` that raises `RELOAD_PAGE` mid-flow while it
   * waits for cart writes, and the page then goes (COM-1521); it moves no
   * `loading` edge, since the push gate leaves it out. So this is a hazard
   * the split removes rather than a bug it fixes.
   * `tests/usePlaceOrderFlow.test.tsx` pins it.
   *
   * A queued `runStep` with `loader: true` does raise one (COM-1296), and the
   * split is what makes that safe: its edges move the push effect's
   * `loading`, never this effect's deps, and the push effect is held back by
   * `isDone` while a before-step runs anyway. The args are read off the
   * entry itself, so they need no dep either.
   *
   * A head runs only while its queue is the store's, and once per queue: see
   * `startedQueues` (COM-1447).
   */
  useEffect(() => {
    const currentAction = actions[0]
    // Nothing queued, or the flow has finished
    if (!currentAction || isDone) {
      return
    }
    // A render behind the store: the queue it holds was already replaced
    const isStoreQueue =
      store.getState().cartSlice.placeOrderFlow.actions === actions
    if (!isStoreQueue) {
      return
    }
    // This queue's head has already run, e.g. before a screen its handler
    // pushed mounted this hook again
    if (startedQueues.has(actions)) {
      return
    }
    startedQueues.add(actions)
    // An entry with no string `name` finds nothing below and is skipped,
    // as an unknown string is.
    const { name, args } = toCinnamonAction(currentAction)
    // Own properties only: `name` is config-authored, and a plain index
    // walks the prototype chain, so `__proto__`, `valueOf` or
    // `hasOwnProperty` would return a built-in that throws when called
    // here, inside an effect, and takes checkout to the error boundary.
    const isKnownHook = Object.hasOwn(hooksMap, name)
    // if action is not defined, go to next action
    if (!isKnownHook) {
      placeOrderFlowNext()
      return
    }
    const action = hooksMap[name]
    action(args)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, isDone, dispatch])

  /*
   * Navigate onto `pushAfter`, once nothing is covering the screen.
   *
   * **The `loading` conjunct is COM-1111.** Without it the push ran in the
   * same effect flush that raised the `CREATE_ORDER` loader: the confirm
   * dialog's "yes" emits `createOrder`, `closeDialog` and `placeOrderFlowNext`
   * from one synchronous `processActions` reduce, so the loader and
   * `isDone: true` land in one commit. `Splash` defers its own
   * `setShow(loading)` through a `setTimeout`, so react-router swapped Cart ->
   * OrderConfirmation *first* and the splash then slid up over the screen it
   * had just revealed, and off again. Three screen states where the shopper
   * should see one.
   *
   * Holding here puts the swap underneath an opaque splash instead. The gate
   * releases on `loading`, which `removeLoader` clears one macrotask *ahead*
   * of `Splash`'s own `show` — so the push lands while the cover is still
   * down, and the cover lifts onto a screen that has already rendered.
   * `addLoader({ name: CREATE_ORDER, delay: 0 })` makes that one macrotask;
   * a larger `loadingDelay` only holds the cover up longer, so the ordering
   * does not depend on the value.
   *
   * ⚠️ **Gate on the loader, never on the splash's own transition.** The
   * obvious alternative — wait for the MUI `Slide`'s `onEntered` — deadlocks
   * on a create that settles before the slide finishes: the dialog closes
   * mid-enter, `onEntered` may never fire, and the push is lost. An order
   * created with the shopper left on the cart is COM-858 verbatim. The ticket's
   * own wording ("hold until the Splash `visible === false`") has the same
   * shape of mistake in the other direction: waiting for the splash to *go*
   * puts the swap back on screen, which is the flash this removes.
   *
   * **The hold has one prerequisite: the container must survive it.** This
   * hook's unmount cleanup dispatches `clearPlaceOrderActions`, so anything
   * that tears the cart down between the confirm click and `removeLoader`
   * takes the push with it. Three things could: a route change, a boot loader
   * (`routes/index.tsx` counts those as booting, and `fetchConfig` is gated
   * behind a slug change, so neither `STORE_CONFIG` nor `INITIAL_DATA` is
   * raised mid-checkout), and `globalError` (`manageErrors`' callers all hang
   * off a cart, summary or boot query, none of which is in flight here) —
   * and `routes/index.tsx` renders `<ErrorMessage>` instead of `<Routes>` for
   * that last one, which is the right outcome anyway. Derived from the call
   * sites rather than observed. Adding another way to unmount the cart
   * mid-create is what would break this.
   *
   * **One route change does happen in that window, and losing the push to it
   * is the point.** A `createOrder` refused by a checkout rule redirects to
   * the rule's screen (`redirectToUnmetRule`) before the `finally` drops the
   * loader, and the shopper must end there, not on `pushAfter`. This hook's
   * cleanup does not guarantee that: it runs too late on a concurrent root,
   * and not at all on the legacy root when the rule screen keeps the cart's
   * template. `redirectToUnmetRule` clears the flow just before it navigates,
   * so this gate is already closed when `loading` moves (COM-1358; the
   * mechanism is in `.claude/CLAUDE.md`). A rejection that does not redirect
   * leaves the flow alone, and the push still lands on the `failed` screen.
   *
   * **An after-step's `WORKFLOW_STEP` does not hold the push (COM-1296).** On
   * a create that settles before `createAndConfirmOrder` returns, `settle`
   * starts the after-order steps while the cart is still mounted, so the
   * first step's `WORKFLOW_STEP` goes up before `CREATE_ORDER` comes down.
   * Were it in the gate, the push would wait for the step to answer, and
   * `runStep` drops its loader and runs `onOutcome` in one task, *before*
   * this effect re-runs: the handlers would forward to the cart's emitter,
   * and on the legacy root an `onOutcome` `pushView` would land first and
   * skip the confirmation screen. Leaving it out puts the push underneath the
   * splash as soon as `CREATE_ORDER` drops, while `WORKFLOW_STEP` keeps
   * `Splash` up (it still reads `defaultSlice.loading`), so COM-1111's cover
   * holds and the step's handlers can run on the confirmation screen. That
   * they wait for it is `useOrderConfirmation`'s doing (`beforeHandlers`): a
   * step can answer before this push lands. Before-steps are unaffected:
   * `isDone` is false while they run. `routes/tests/
   * afterOrderSyncSettleNavigation.test.tsx` pins it.
   *
   * **Only an after-step's.** The exclusion holds while
   * `ordersSlice.afterOrderRunning` is set, which `runAfterOrder` does only
   * when it starts with this push still due, and clears once that list no
   * longer needs it. Any other `WORKFLOW_STEP` holds the push like every
   * other loader: a button's own `runStep` still waiting when the queue
   * finishes keeps the cart, and the emitter its `onOutcome` runs on, up
   * until it answers, rather than pushing away and leaving its handlers to a
   * dead emitter.
   *
   * **One behaviour change off the opted-in path.** `useCreateOrder` drops the
   * loader in its `finally` when `options.inWebviewOrderConfirmation` is
   * authored, but on the legacy path only in the `catch` — success assigns
   * `window.location` and takes the document. So a legacy storefront that
   * authored `pushAfter` no longer pushes on success. That push landed on a
   * screen `templates/OrderConfirmation.tsx` redirects away from (COM-819)
   * moments before the document went, so this is one flash fewer, not a lost
   * destination. Its error path is unchanged: it still pushes, and COM-819
   * still redirects.
   */
  useEffect(() => {
    if (!isDone || !pushAfter || loading) return
    pushView({ path: pushAfter })
    dispatch(clearPlaceOrderActions())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDone, pushAfter, loading, dispatch])

  // clear place order actions on unmount
  useEffect(() => {
    return () => {
      dispatch(clearPlaceOrderActions())
    }
  }, [dispatch])

  return { placeOrderFlow, placeOrderFlowNext, placeOrderFlowClear }
}

export default usePlaceOrderFlow
